/**
 * Empire-PWA chassis · on-device LLM via transformers.js.
 *
 * Dial uses text-only models. The full Phi-3-mini-onnx-web is deferred to v1.1.
 * v0 ships a deterministic intent-tagger so the voice-memo and contact-import
 * flows can ship without the 1 GB model download.
 */

export interface IntentTag {
  readonly intent: 'call' | 'remind' | 'gift' | 'note' | 'meet' | 'unknown';
  readonly target: string | null;
  readonly confidence: number;
}

const INTENTS: Array<{ rx: RegExp; intent: IntentTag['intent'] }> = [
  { rx: /\b(call|phone|dial)\s+([\w\s]+?)(?:\s+(?:please|now|today)|$|[.!?])/i, intent: 'call' },
  { rx: /\b(remind\s+(?:me\s+)?(?:to\s+)?call|nudge|chase)\s+([\w\s]+?)(?:\s|$|[.!?])/i, intent: 'remind' },
  { rx: /\b(gift|give|present|bhej(?:o|na))\s+([\w\s]+?)(?:\s|$|[.!?])/i, intent: 'gift' },
  { rx: /\b(meet|coffee|lunch|dinner|milte)\s+([\w\s]+?)(?:\s|$|[.!?])/i, intent: 'meet' },
];

export function tagIntent(text: string): IntentTag {
  for (const { rx, intent } of INTENTS) {
    const m = text.match(rx);
    if (m) {
      return { intent, target: (m[2] ?? '').trim() || null, confidence: 0.6 };
    }
  }
  return { intent: text.length > 0 ? 'note' : 'unknown', target: null, confidence: 0.3 };
}

export function cosine(a: Float32Array, b: Float32Array): number {
  let s = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) s += a[i]! * b[i]!;
  return s;
}

export async function canUseWebGPU(): Promise<boolean> {
  const nav = navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } };
  if (!nav.gpu) return false;
  try {
    const adapter = await nav.gpu.requestAdapter();
    return adapter !== null;
  } catch {
    return false;
  }
}
